import { X } from "lucide-react";
import * as DialogPrimitive from "radix-ui/dialog";
import { useRef, type ComponentProps, type HTMLAttributes } from "react";
import { cn } from "./cn";
import { Icon } from "./icon";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn("fixed inset-0 z-40 bg-[oklch(0.1_0.01_250/0.45)]", className)}
      {...props}
    />
  );
}

/**
 * Send focus back where it came from when the dialog closes.
 *
 * Radix returns focus to its own `Dialog.Trigger`; almost every dialog here is opened from a plain
 * button with a controlled `open` prop instead, so that ref is null and focus lands on `<body>` —
 * a keyboard user is dropped at the top of the page. `onOpenAutoFocus` fires while the opener is
 * still the active element, so remembering it there and restoring it in `onCloseAutoFocus` returns
 * focus for every dialog and sheet, trigger or no trigger (design-system §8, rules/01 §12).
 */
export function useReturnFocus(
  props: Pick<ComponentProps<typeof DialogPrimitive.Content>, "onOpenAutoFocus" | "onCloseAutoFocus">,
) {
  const opener = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: (event: Event) => {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
      props.onOpenAutoFocus?.(event);
    },
    onCloseAutoFocus: (event: Event) => {
      props.onCloseAutoFocus?.(event);
      if (event.defaultPrevented) return;
      const el = opener.current;
      opener.current = null;
      // A virtualised row can be recycled while the sheet is open; then there is nothing to go back to.
      if (!el?.isConnected) return;
      event.preventDefault();
      el.focus();
    },
  };
}

interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  /** Width preset: md for forms (max-w-lg), lg for media (max-w-4xl). */
  /**
   * `wide` exists for one thing: the permission matrix, which is 16 modules by 8 actions. At `lg` it
   * scrolled sideways, and a grid you cannot see at once is a grid you cannot reason about.
   */
  width?: "md" | "lg" | "wide";
}

/** Dialog: radius-lg, border, the only place (with popovers) that carries --shadow-sm. Focus is trapped by Radix. */
export function DialogContent({ className, children, width = "md", ...props }: DialogContentProps) {
  const returnFocus = useReturnFocus(props);
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-auto rounded-lg border border-border bg-surface-raised p-6 shadow-sm",
          width === "md" ? "max-w-lg" : width === "lg" ? "max-w-4xl" : "max-w-6xl",
          className,
        )}
        {...props}
        {...returnFocus}
      >
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-sm text-fg-muted transition-colors duration-120 hover:bg-surface-hover hover:text-fg"
          aria-label="Close"
        >
          <Icon icon={X} size={16} />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex flex-col gap-1 pr-8", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("text-section font-semibold text-fg", className)} {...props} />;
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={cn("text-cell text-fg-muted", className)} {...props} />;
}
